import { pxToEm } from 'util/pxToEm';
import React, { ReactElement } from 'react';
import { Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { CalendarIcon } from 'components/icons';
import type { DateFilterPreset } from 'hooks/useAnalyticsDateFilter';

export interface AnalyticsDateFilterTriggerProps {
  preset: DateFilterPreset;
  startDate: Date | null;
  endDate: Date | null;
  onClick: () => void;
  /** When true, chip stretches full width on xs (executive analytics toolbar) */
  fullWidthOnMobile?: boolean;
  sx?: SxProps<Theme>;
}

function formatFilterLabel (
  preset: DateFilterPreset,
  startDate: Date | null,
  endDate: Date | null,
): string {
  if (preset === 'all-dates') return 'All Dates';
  if (preset === 'current-semester') return 'Current Semester';
  if (preset === 'this-academic-year') return 'This Academic Year';
  if (preset === 'custom' && startDate && endDate) {
    return `${startDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })} – ${endDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })}`;
  }
  return 'This Academic Year';
}

export const AnalyticsDateFilterTrigger = ({
  preset,
  startDate,
  endDate,
  onClick,
  fullWidthOnMobile: _fullWidthOnMobile = false,
  sx: sxProp,
}: AnalyticsDateFilterTriggerProps): ReactElement => (
  <Box
    display="flex"
    gap={8}
    alignItems="center"
    onClick={onClick}
    sx={[
      {
        width: 250,
        height: 50,
        cursor: 'pointer',
        border: '1px solid #DEDEDE',
        borderRadius: '8px',
        padding: '8px 16px',
        backgroundColor: '#FFFFFF',
        '&:hover': {
          backgroundColor: '#F5F5F5',
        },
      },
      ...(sxProp ? (Array.isArray(sxProp) ? sxProp : [sxProp]) : []),
    ]}
  >
    <CalendarIcon style={{ width: 16, height: 16 }} />
    <Typography
      color="text.primary"
      fontSize={pxToEm(14)}
      fontWeight={400}
      lineHeight={pxToEm(20)}
      sx={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {formatFilterLabel(preset, startDate, endDate)}
    </Typography>
  </Box>
);
